import { TrendingUp, ShieldCheck, Brain, Crosshair, Sigma, LineChart } from "lucide-react";

const PILLARS = [
  {
    icon: Brain,
    title: "Numbers, not guesses.",
    body:
      "Every recommendation is based on real data — rent prices, demand, costs. We don't ‘feel’ the market. We check it.",
  },
  {
    icon: ShieldCheck,
    title: "Your money is safe first.",
    body:
      "Before we look at how much you can make, we check how much you could lose. We only move forward when the risk is fair.",
  },
  {
    icon: Crosshair,
    title: "High-Yield Property Insights (Up to 17% Rental Yield)",
    body:
      "We analyse off-market opportunities and highlight properties with potential yields of up to 17%. Our focus is on uncovering high-performing investment trends and opportunities that are often not visible in the public market, giving investors clearer insight into strategies and deals most overlook.",
  },
  {
    icon: Sigma,
    title: "Tested for the worst case.",
    body:
      "We run 100+ \"what if\" scenarios before recommending any deal — stress-testing cash flow, vacancy risk, interest rate rises, and market downturns. If a property can't withstand a tough year under conservative assumptions, it doesn't make the shortlist.",
  },
  {
    icon: LineChart,
    title: "Easy-to-read reports.",
    body:
      "Monthly updates in plain English. Numbers explained, not hidden. You always know where your money is and why.",
  },
  {
    icon: TrendingUp,
    title: "Built to grow steadily.",
    body:
      "We plan years ahead — using tax savings and smart timing to help your money grow without big surprises.",
  },
];

export default function WhyAxiom() {
  return (
    <section
      id="how-we-think"
      data-testid="thesis-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#0A0A0A] grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 lg:mb-24">
          <div className="lg:col-span-4">
            <div className="data-label mb-6">/ How We Think</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Wealth isn't built on <span className="italic text-white/60">hope</span>.
              <br />
              It's built on <span className="text-gold">simple math</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 text-white/70 text-lg leading-relaxed space-y-5">
            <p>
              Most people lose money in property because they buy based on a story.
              ‘This area is hot.’ ‘My friend made $100k here.’ Stories don't pay the
              mortgage.
            </p>
            <p>
              Axiom is for people who want straight answers — not sales talk.
            </p>
            <p>
              No guesswork, no complexity — just senior expert analysis across
              <span className="text-gold"> 25+ data sources</span> and
              <span className="text-gold"> 100+ data points</span>, delivered in a
              simple, easy-to-use presentation so you can make informed decisions
              with confidence.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/5">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`pillar-${i}`}
                className="group relative p-10 lg:p-12 border-r border-b border-white/5 transition-colors hover:bg-[#0F0F0F]"
              >
                <div className="absolute top-6 right-6 font-mono text-[10px] tracking-[0.3em] text-white/30">
                  {`0${i + 1}`}
                </div>
                <Icon className="text-gold mb-8" size={28} strokeWidth={1.4} />
                <h3 className="font-display text-2xl sm:text-3xl leading-tight mb-4">
                  {p.title}
                </h3>
                <p className="text-white/65 leading-relaxed text-[15px]">{p.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
